import { useState, useEffect } from "react";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { Menu, X, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { supabaseAuth } from "../../supabaseClient";

const LandingNav = ({ onLoginClick, externalSession }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [googleSession, setGoogleSession] = useState(externalSession || null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  // Update internal state when external session changes
  useEffect(() => {
    if (externalSession !== undefined) {
      setGoogleSession(externalSession);
      setLoading(false);
    }
  }, [externalSession]);

  // Check Google session on mount (fallback jika externalSession ga ada)
  useEffect(() => {
    if (externalSession === undefined) {
      const checkSession = async () => {
        const {
          data: { session },
        } = await supabaseAuth.auth.getSession();
        if (session?.user) {
          setGoogleSession(session);
          // Ensure profile exists
          await ensureCustomerProfile(session.user);
        }
        setLoading(false);
      };
      checkSession();

      const {
        data: { subscription },
      } = supabaseAuth.auth.onAuthStateChange(async (event, session) => {
        if (session?.user) {
          await ensureCustomerProfile(session.user);
        }
        setGoogleSession(session);
      });

      return () => subscription.unsubscribe();
    }
  }, [externalSession]);

  // Ensure customer profile exists
  const ensureCustomerProfile = async (user) => {
    try {
      // Gunakan maybeSingle() untuk avoid error ketika data tidak ada
      const { data: existing, error: selectError } = await supabaseAuth
        .from("customer_profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();

      // Ignore PGRST116 error (row not found) - ini normal
      if (selectError && selectError.code !== "PGRST116") {
        console.error("Error checking profile:", selectError);
        return; // Return early jangan block session
      }

      if (!existing) {
        const { error: insertError } = await supabaseAuth
          .from("customer_profiles")
          .insert({
            id: user.id,
            email: user.email,
            full_name:
              user.user_metadata?.full_name || user.user_metadata?.name,
            avatar_url: user.user_metadata?.avatar_url,
          });

        if (insertError) {
          console.error("Error creating profile:", insertError);
          // Jangan throw error, biar session tetap persist
        }
      }
    } catch (err) {
      console.error("Error ensuring customer profile:", err);
      // Jangan throw error, biar session tetap persist
    }
  };

  const handleLogout = async () => {
    await supabaseAuth.auth.signOut();
    setGoogleSession(null);
  };

  const navItems = [
    { label: "Beranda", href: "/", isPage: true },
    { label: "Paket", href: "/paket", isPage: true },
    { label: "Gallery", href: "/gallery", isPage: true, comingSoon: true },
    { label: "Claim Foto", href: "/claim-foto", isPage: true },
    { label: "Kontak", href: "/kontak", isPage: true, comingSoon: true },
  ];

  return (
    <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-4xl">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-background/70 backdrop-blur-xl border border-border/50 rounded-2xl shadow-lg shadow-black/5"
      >
        <div className="px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <a href="#beranda" className="flex items-center gap-2">
              <img
                src="/stiker logo snapme.png"
                alt="SnapMe"
                className="h-8 w-auto object-contain"
              />
            </a>

            {/* Desktop Navigation */}
            {!isMobile && (
              <div className="flex items-center gap-1">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.comingSoon) {
                        alert("Coming Soon! 🚀");
                      } else {
                        window.location.href = item.href;
                      }
                    }}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-muted/50 transition-all"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}

            {/* CTA Button */}
            <div className="flex items-center gap-2">
              {!isMobile &&
                !loading &&
                (googleSession ? (
                  <div className="flex items-center gap-2">
                    {googleSession.user?.user_metadata?.avatar_url && (
                      <img
                        src={googleSession.user.user_metadata.avatar_url}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 rounded-xl bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={onLoginClick}
                    className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Login
                  </button>
                ))}

              {/* Mobile Menu Button */}
              {isMobile && (
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-xl hover:bg-muted/50 transition-colors"
                >
                  {mobileMenuOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <Menu className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobile && mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-border/50"
            >
              <div className="px-4 py-3 space-y-1">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                      if (item.comingSoon) {
                        alert("Coming Soon! 🚀");
                      } else {
                        window.location.href = item.href;
                      }
                    }}
                    className="block px-4 py-2.5 rounded-xl text-foreground/70 hover:bg-muted/50 hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (googleSession) {
                      handleLogout();
                    } else {
                      onLoginClick();
                    }
                  }}
                  className="w-full mt-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                >
                  {googleSession ? (
                    <>
                      <LogOut className="w-4 h-4" />
                      Logout
                    </>
                  ) : (
                    "Login"
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </nav>
  );
};

export default LandingNav;
